import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import cx from 'clsx';

import { UnionAvatar, EmptyRecord, Button, VipLogoWithGift } from '@/components';
import { getOfficialPath, isOfficalAddress, openNewWindow } from '@/utils';

import { ArticleComments, ArticleContent } from '../../components';
import { useFeedArticle } from '../../hooks';
import style from './index.module.scss';
import { getDetailPageTitle } from '../../utils';
import { CustomTitle } from '@/components/CustomTitle';

type StreamProps = {
  address?: string;
  id: string;
};
const maxImgSize: [number, number] = [window.screen.width - 40, 260];

export const DetailContainer = memo(({ address, id }: StreamProps) => {
  const { t } = useTranslation();

  const { data: feed, isFetching } = useFeedArticle(id);
  const history = useHistory();
  const titlePrefix = getDetailPageTitle(feed?.article);

  const handleClick = useCallback(() => {
    history.replace('/stream');
  }, [history]);

  const handleBotClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (feed?.article?._isBot && feed?.article?.monitor_alert?.bot?.is_active) {
        openNewWindow(`/bot/${feed?.article?.monitor_alert?.bot?.id}`, '_blank');
      }
    },
    [feed],
  );

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (!feed) {
        return;
      }
      const userId = feed?.article.creator_id;
      const isOffical = isOfficalAddress(userId);
      // path上officialId不带0a；

      openNewWindow(
        isOffical
          ? `${getOfficialPath(feed?.article?.creator as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [feed],
  );

  const needPaid = useMemo(() => {
    const payThread =
      feed?.article.type === 'pay_thread' &&
      !feed?.article.is_paid &&
      feed?.article.creator_id !== address;

    const payQa =
      feed?.article.type === 'pay_qa' &&
      feed?.article.pay_qa?.answerer?.id !== address &&
      feed?.article.creator_id !== address &&
      !feed?.article.pay_qa?.is_paid;

    // return !!address && (payThread || payQa);
    return payThread || payQa;
  }, [feed?.article, address]);

  return (
    <>
      <CustomTitle titlePrefix={titlePrefix} />
      <div className={style.stream}>
        {!isFetching && (!feed || feed.article.is_withdrawn) ? (
          <EmptyRecord
            className={style.emptyContainer}
            content={
              <>
                <div>
                  {feed?.article.is_withdrawn
                    ? t('DetailContainer.hidden')
                    : t('DetailContainer.noexist')}
                </div>
                <div>{t('DetailContainer.he82q')}</div>
                <Button type="primary" className={style.gotoBtn} onClick={handleClick}>
                  {t('DetailContainer.19uqzf1')}
                </Button>
              </>
            }
          />
        ) : (
          <div className={cx(style.streamMain, feed?.article.is_withdrawn && style.isWithdrawn)}>
            {isFetching ? (
              <>
                <div className={style.streamHead}>
                  <Skeleton width={38} height={38} circle className={style.avatar} />
                  <div className={style.creator}>
                    <Skeleton className={style.uidText} />
                    <Skeleton className={style.createdAt} />
                  </div>
                </div>
                <Skeleton className={style.content} height={300} />
              </>
            ) : feed ? (
              <div className={style.streamHead}>
                <div onClick={feed.article._isBot ? handleBotClick : handleUserClick}>
                  <UnionAvatar
                    className={style.avatar}
                    size={38}
                    data={feed.article.creator}
                    isBot={feed.article._isBot}
                    isBotActive={feed.article.monitor_alert?.bot?.is_active}
                  />
                </div>
                <div className={style.header}>
                  <div className={style.creator}>
                    <div className={style.creatorLeft}>
                      <div
                        onClick={feed.article._isBot ? handleBotClick : handleUserClick}
                        className={cx(
                          style.author,
                          feed.article._isBot &&
                            !feed?.article?.monitor_alert?.bot?.is_active &&
                            style.isOffline,
                        )}>
                        {feed.article._author}
                      </div>
                      {feed.article._showVip ? (
                        <VipLogoWithGift
                          userInfo={feed.article.creator as UserInfo}
                          marginRight={8}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className={style.createdAt}>{feed.article._fulltime}</div>
                </div>
              </div>
            ) : null}
            {feed?.article ? (
              <ArticleContent
                articleDetail={feed}
                className={style.content}
                showRawImage
                source="detail"
                maxImgSize={maxImgSize}
                contentClassName={style.articleContent}
                enableReadFeedback={true}
              />
            ) : null}
            {feed ? (
              <ArticleComments
                containerClassName={style.commentsList}
                feed={feed}
                articleId={Number(id)}
                disabled={!feed || feed.article.is_withdrawn || needPaid}
                needPaid={needPaid}
              />
            ) : null}
          </div>
        )}
      </div>
    </>
  );
});
